import {
  clearPendingSso,
  exchangeLoginCode,
  readPendingSso,
  ssoRedirectUri,
} from "@/features/auth/api/sso";

import { setAuthStatus } from "./auth-status";
import { finishPendingLogout, LOGOUT_PENDING_CODE } from "./logout";
import { startSession } from "./session";

export type SsoCallbackState = {
  readonly exchanging: boolean;
  readonly error: string | null;
};

export const SSO_CALLBACK_BAD_REQUEST = "bad_request";

const LOGIN_PATH = "/login";
const IDLE_STATE: SsoCallbackState = { exchanging: false, error: null };

let state: SsoCallbackState = IDLE_STATE;
const listeners = new Set<() => void>();

function setState(next: SsoCallbackState) {
  state = next;
  for (const listener of listeners) {
    listener();
  }
}

export function getSsoCallbackState(): SsoCallbackState {
  return state;
}

export function subscribeToSsoCallback(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function clearSsoCallbackError() {
  if (state.error !== null) {
    setState({ ...state, error: null });
  }
}

function takeCallbackParams(): { loginCodes: string[]; errors: string[] } | null {
  if (window.location.pathname !== LOGIN_PATH) {
    return null;
  }
  const params = new URLSearchParams(window.location.search);
  const loginCodes = params.getAll("login_code");
  const errors = params.getAll("error");
  if (loginCodes.length === 0 && errors.length === 0) {
    return null;
  }
  params.delete("login_code");
  params.delete("error");
  const query = params.toString();
  window.history.replaceState(
    window.history.state,
    "",
    `${LOGIN_PATH}${query ? `?${query}` : ""}${window.location.hash}`,
  );
  return { loginCodes, errors };
}

function fail(code: string): false {
  clearPendingSso();
  setState({ exchanging: false, error: code });
  return false;
}

export async function completeSsoCallback(): Promise<boolean> {
  const receivedAt = performance.now();
  const callback = takeCallbackParams();
  if (callback === null) {
    return false;
  }
  if (import.meta.env["VITE_MOCK_AUTH"] === "true") {
    clearPendingSso();
    return false;
  }
  const { loginCodes, errors } = callback;
  const [loginCode] = loginCodes;
  const [error] = errors;
  if (loginCodes.length + errors.length !== 1) {
    return fail(SSO_CALLBACK_BAD_REQUEST);
  }
  if (error !== undefined) {
    return fail(error);
  }
  const pending = readPendingSso();
  if (loginCode === undefined || pending?.redirectUri !== ssoRedirectUri()) {
    return fail(SSO_CALLBACK_BAD_REQUEST);
  }

  setAuthStatus("restoring");
  setState({ exchanging: true, error: null });
  if (!(await finishPendingLogout())) {
    return fail(LOGOUT_PENDING_CODE);
  }
  const result = await exchangeLoginCode(loginCode, pending.codeVerifier, receivedAt);
  if (!result.ok) {
    return fail(result.code);
  }
  clearPendingSso();
  startSession(result.tokens);
  setState(IDLE_STATE);
  return true;
}
